import React, { useState, useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import { BrowserRouter as Router } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { setAuthContext } from './utils/apiClient';
import FileUpload from './components/FileUpload';
import CandidateTable from './components/CandidateTable';
import CandidateModal from './components/CandidateModal';
import ProcessingStats from './components/ProcessingStats';
import PositionSummary from './components/PositionSummary';
import UserProfile from './components/UserProfile';
import LoginButton from './components/LoginButton';
// import ProtectedRoute from './components/ProtectedRoute'; // DISABLED - No authentication required

// Main App Component
const AppContent = () => {
  console.log('🚨 AppContent: COMPONENT RENDERING/RE-RENDERING!');
  
  const [candidates, setCandidates] = useState([]);
  const [positionRequirements, setPositionRequirements] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const auth = useAuth();
  
  console.log('🔍 AppContent: Auth state -', {
    isAuthenticated: auth.isAuthenticated,
    loading: auth.loading,
    user: auth.user?.email,
    token: auth.token ? `${auth.token.substring(0, 20)}...` : null,
    ssoEnabled: auth.ssoEnabled
  });

  // Set auth context for API client - ENABLED for analytics
  useEffect(() => {
    console.log('🔍 AppContent: Setting auth context for API client');
    setAuthContext(auth);
  }, [auth]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUploadSuccess = (data) => {
    setCandidates(data.candidates);
    
    // Set position requirements from upload response (IMMEDIATE DISPLAY!)
    if (data.position_requirements) {
      setPositionRequirements(data.position_requirements);
      console.log('Position requirements set:', data.position_requirements);
    }
    
    setSuccess(`Successfully processed ${data.phf_count} PHF files and ${data.post_count} post description files`);
    setError('');
  };

  const handleUploadError = (errorMessage) => {
    setError(errorMessage);
    setSuccess('');
    setCandidates([]);
    setPositionRequirements({});  // Clear position requirements on error
  };

  const handleCandidateClick = async (candidate) => {
    setSelectedCandidate(candidate);
    setShowModal(true);
    
    // If we don't have position requirements yet, fetch them from the candidate details
    if (Object.keys(positionRequirements).length === 0) {
      try {
        const { api } = await import('./utils/apiClient');
        const response = await api.getCandidateDetails(candidate.id);
        if (response.data.success && response.data.position_requirements) {
          setPositionRequirements(response.data.position_requirements);
        }
      } catch (error) {
        console.error('Failed to fetch position requirements:', error);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCandidate(null);
  };

  const req = positionRequirements || {};

  return (
    <div className="App">
      {/* The one HR | OneWHO AI header (shared by every HR app): service first, family last, no WHO logo. */}
      <header className="owa-header">
        <div className="owa-header__start">
          <span className="owa-lockup">
            <span className="owa-lockup__service">HR</span>
            <span className="owa-lockup__divider" aria-hidden="true"></span>
            <span className="owa-wordmark" lang="en">OneWHO <b>AI</b></span>
          </span>
          <span className="owa-header__tool">Step Determination</span>
        </div>
        {auth.isAuthenticated && (
          <div className="owa-header__end">
            <UserProfile />
          </div>
        )}
      </header>

      {/* Hero */}
      <div className="hero">
        <div className="hero-inner">
          <div className="eyebrow">Human Resources · Talent &amp; Grading</div>
          <h1>WHO Step Determination Analysis</h1>
          <p>
            Upload PHF documents and job descriptions to analyze candidate qualifications
            and generate comprehensive step-determination reports.
          </p>
          <div className="hero-stats">
            <div className="hs"><b>{candidates.length}</b><span>Candidates processed</span></div>
            <div className="hs"><b>{req.grade || '—'}</b><span>Target grade</span></div>
            <div className="hs"><b>{req.required_experience_years ? `${req.required_experience_years} yrs` : '—'}</b><span>Required experience</span></div>
          </div>
        </div>
      </div>

      <div className="wrap" style={{ flex: 1 }}>
        {/* Alert Messages */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mt-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mt-3">
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {auth.isAuthenticated ? (
          <>
            <FileUpload
              onUploadSuccess={handleUploadSuccess}
              onUploadError={handleUploadError}
              loading={loading}
              setLoading={setLoading}
            />
            <ProcessingStats />
            {candidates.length > 0 && (
              <PositionSummary positionRequirements={positionRequirements} candidates={candidates} />
            )}
            {candidates.length > 0 && (
              <CandidateTable
                candidates={candidates}
                onCandidateClick={handleCandidateClick}
              />
            )}
          </>
        ) : (
          !auth.loading && (
            <div className="login-screen">
              <div className="login-card">
                <div className="lock"><i className="fas fa-lock"></i></div>
                <h2>Authentication required</h2>
                <p>Sign in with your Microsoft account to access the WHO Step Determination System.</p>
                <LoginButton className="btn btn-ms" />
                <div className="login-foot">
                  <i className="fas fa-shield-halved me-1"></i>
                  Single sign-on · worldhealthorg.onmicrosoft.com
                </div>
              </div>
            </div>
          )
        )}

        {/* Candidate Detail Drawer */}
        <CandidateModal
          show={showModal}
          onHide={handleCloseModal}
          candidate={selectedCandidate}
        />
      </div>

      {/* Footer */}
      <footer className="appfoot">
        © 2026 WHO Step Determination System · World Health Organization
      </footer>
    </div>
  );
};

// Main App wrapper with providers
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
